import React, { useContext, useEffect, useState } from "react";
import { injectIntl } from "react-intl";

import EventLogItemExtra from "./EventLogItemExtra";

import { lightOrDark } from "../../../Base/Utilities/LightOrDark";
import { getEventTranslationKey } from "../../../Base/Utilities/TranslationHelper";
import PlayerSelectEditor from "../PlayerSelectEditor/PlayerSelectEditor";
import EventLogDropdown from "../EventLogDropdown/EventLogDropdown";
import { sendEvent } from "../../../Base/Utilities/SendEvent";
import { useCanEditDropdown } from "../../../Base/Hooks/canEditDropdown";
import EventEditDropdown from "../EventEditDropdown/EventEditDropdown";
import { HoverEventContext } from "../../../../Config/HoverEvent";
import { FixtureDetailsContext } from "../../../../Config/FixtureDetails.jsx";
import { teamAssignEnabledEvents } from "./EventTeamAssignConfig.js";
import {
  getRelatedClockEventsByPlayId, getRemainingTimeFromSuspension,
} from "../../../Base/Utilities/Suspension.js";
import { createBaseEvent } from "../../../Base/Utilities/CreateBaseEvent.js";

const EventLogItem = (props) => {
  const { event, eventStore, currentState, intl, extraPlayInfo } = props;

  const { formatMessage } = intl;
  const [team, setTeam] = useState();
  const [player, setPlayer] = useState();
  const [staffRole, setStaffRole] = useState(null);
  const [eventKey, setEventKey] = useState();
  const [isPending, setIsPending] = useState();
  const [updatePlayer, setUpdatePlayer] = useState(true);
  const [showPlayerSelect, setShowPlayerSelect] = useState(false);
  const [canEdit, editFlow] = useCanEditDropdown(props);
  const [showEditDropdown, setShowEditDropdown] = useState(false);
  const { hoverStart, hoverEnd } = useContext(HoverEventContext);
  const { sport, fixtureProfile } = useContext(FixtureDetailsContext);

  const ignoreBibClickEvents = ["timeOut", "videoReview", "powerPlay"];

  const isTeamAssignVisible = () => {
    return (teamAssignEnabledEvents[sport] ?? []).includes(event.eventType);
  };

  const handleBibClick = () => {
    if (showPlayerSelect) {
      setShowPlayerSelect(false);
    } else {
      const ignored = ignoreBibClickEvents.includes(event.eventType);
      if (!ignored) {
        setShowPlayerSelect(true);
      }
    }
  };

  const alterGoalKeeperEvent = (person) => {
    const goalEvent = eventStore.find((e) => e.eventId === event.eventId);
    if (!goalEvent) {
      return null;
    }
    goalEvent.options["goalKeeperId"] = person.personId;
    return {
      action: "sendEvent",
      event: {
        ...goalEvent,
        status: "updated",
      },
      type: "custom",
    };
  };

  const handlePlayerChange = (person) => {
    setShowPlayerSelect(false);
    let sendAction;
    if (event.eventType === "goalKeeper") {
      sendAction = alterGoalKeeperEvent(person);
    } else {
      sendAction = {
        action: "sendEvent",
        event: {
          ...event,
          status: "updated",
          personId: person.personId,
        },
        type: "custom",
      };
    }
    if (sendAction) {
      sendEvent(sendAction, props);
    }
  };

  const handleTeamAssign = () => {
    setShowPlayerSelect(false);
    delete event.personId;
    const sendAction = {
      action: "sendEvent",
      event: {
        ...event,
        status: "updated",
        entityId: team.entityId,
      },
      type: "custom",
    };
    sendEvent(sendAction, props);
  };

  const toggleEditDropdown = () => {
    setShowEditDropdown(!showEditDropdown);
  };

  const handleHover = (mouseInside) => {
    if (mouseInside) {
      hoverStart(event);
    } else {
      hoverEnd();
    }
  };

  useEffect(() => {
    const entity = currentState.entities.find((el) => el.entityId === event.entityId);
    setTeam(entity);
    setUpdatePlayer(true);

    setEventKey(getEventTranslationKey(event, sport, extraPlayInfo));

    // Double Check pending status
    let pending = !!event.pending;
    if (currentState.acks && currentState.acks.findIndex((item) => item === event.eventId) > -1) {
      pending = false;
    }
    setIsPending(pending);
    // eslint-disable-next-line
  }, [currentState.updateLog]);

  useEffect(() => {
    if (team && updatePlayer) {
      setPlayer(team.persons.find((el) => el.personId === event.personId));

      if (sport === "handball") {
        setStaffRole(
          Object.keys(team.staffState).filter((role) => team.staffState[role].personId === event.personId)[0],
        );
      }

      setUpdatePlayer(false);
    }
    // eslint-disable-next-line
  }, [team, updatePlayer]);


  // sends a suspension stop event when the clock ends, but only for suspensions that do not have a personId
  useEffect(() => {

    if (sport !== "handball" || event.eventType !== "suspension" || event.personId) {
      return;
    }

    const clockEvents = getRelatedClockEventsByPlayId({
      playId: event.playId,
      events: eventStore ?? [],
    });

    const remainingTime = getRemainingTimeFromSuspension(
      event && clockEvents.start && !clockEvents.stop
        ? {
          ...event,
          clock: clockEvents.start.clock,
          options: clockEvents.adjust?.options ?? {},
        }
        : null,
      currentState.clock?.displayTime,
      currentState.period.periodId,
      fixtureProfile,
      currentState.period.periodStatus,
    );

    if (clockEvents?.start && !clockEvents?.stop && remainingTime <= 1) {
      const action = createBaseEvent(currentState);
      const clockParts = currentState.clock.displayTime.split(":");
      action.event.class = "clock";
      action.event.eventType = "suspension";
      action.event.subType = "stop";
      action.event.entityId = event.entityId;
      action.event.clock = `PT${parseInt(clockParts[0])}M${parseInt(clockParts[1])}S`;
      action.event.playId = event.playId;

      sendEvent(action, props);
    }

  }, [currentState.clock]);

  if (event.class === "clock" && event.eventType.includes("Card")) {
    return null;
  }

  return (
    <React.Fragment>
      {team && (
        <div
          className={"log-event pending-" + isPending}
          onMouseEnter={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          <span
            onClick={handleBibClick}
            className={"event-bib " + (team && lightOrDark(team.primaryColor))}
            style={
              team && {
                backgroundColor: team.primaryColor,
              }
            }
          >
            {player && player.bib} {!player && " "}
            {staffRole &&
              formatMessage({
                id: `role.abbrev.${staffRole}`,
                defaultMessage: `role.abbrev.${staffRole}`,
              })}{" "}
            {!staffRole && " "}
          </span>
          {showPlayerSelect && (
            <EventLogDropdown
              onOutsideClick={() => setShowPlayerSelect(false)}
              containerStyle={{
                top: "3vh",
              }}
              bridgeStyle={{
                width: "1.8vw",
              }}
            >
              <PlayerSelectEditor
                {...props}
                event={event}
                eventStore={props.eventStore}
                intl={intl}
                sport={sport}
                team={team}
                showTeamAssign={isTeamAssignVisible()}
                onPlayerSelect={handlePlayerChange}
                onTeamAssign={handleTeamAssign}
              />
            </EventLogDropdown>
          )}
          <span onClick={toggleEditDropdown} className="event-type">
            {formatMessage({
              id: eventKey,
              defaultMessage: eventKey,
            })}
            <EventLogItemExtra event={event} eventStore={props.eventStore} />
          </span>
        </div>
      )}
      {!team && eventKey && (
        <div className="log-event" onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)}>
          <span
            className="event-bib"
            style={{
              backgroundColor: "transparent",
            }}
          >
            {!player && " "}
          </span>
          <span onClick={toggleEditDropdown} className="event-type">
            {formatMessage({
              id: eventKey,
              defaultMessage: eventKey,
            })}
            {event.eventType === "videoReview" && (
              <EventLogItemExtra event={event} eventStore={props.eventStore} />
            )}
          </span>
        </div>
      )}
      {canEdit && showEditDropdown && (
        <EventEditDropdown
          editFlow={editFlow}
          onClose={() => {
            setShowEditDropdown(false);
          }}
          {...props}
        />
      )}
    </React.Fragment>
  );
};

export default injectIntl(EventLogItem);
